import { AppAction, ReportType, UserRole } from "@edlab-fms/shared";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuthQuery } from "../lib/api/auth";
import { useDonorsQuery } from "../lib/api/donors";
import { useProgrammesOverviewQuery } from "../lib/api/overview";
import { ReportPreviewDisplay } from "../features/reports/ReportPreviewDisplay";
import {
  buildReportFilters,
  useExportReportMutation,
  useReportPreviewQuery,
} from "../lib/api/reports";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Chip, ChipGroup } from "../components/ui/Chip";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Modal } from "../components/ui/Modal";
import { MonthSelector } from "../components/ui/MonthSelector";
import { useReportingPeriod } from "../lib/hooks/useReportingPeriod";
import { PageHeader } from "../components/ui/PageHeader";

const REPORT_OPTIONS: {
  id: ReportType;
  label: string;
  description: string;
}[] = [
  {
    id: ReportType.DONOR,
    label: "Donor update",
    description: "Funding received, used and remaining",
  },
  {
    id: ReportType.BOARD,
    label: "Board summary",
    description: "The whole picture, clearly presented",
  },
  {
    id: ReportType.PROGRAMME,
    label: "Programme financials",
    description: "Monthly and year-to-date detail",
  },
  {
    id: ReportType.TRANSACTIONS,
    label: "Transaction export",
    description: "Line-level receipts and payments",
  },
];

function parseReportType(
  value: string | null,
  role: UserRole | undefined,
): ReportType {
  if (value === ReportType.DONOR) {
    return ReportType.DONOR;
  }
  if (value === ReportType.PROGRAMME) {
    return ReportType.PROGRAMME;
  }
  if (value === ReportType.TRANSACTIONS) {
    return ReportType.TRANSACTIONS;
  }
  if (value === ReportType.BOARD) {
    return ReportType.BOARD;
  }
  if (role === UserRole.BOARD_MEMBER) {
    return ReportType.BOARD;
  }
  return ReportType.DONOR;
}

export function ReportsPage() {
  const auth = useAuthQuery();
  const user = auth.data;
  const donors = useDonorsQuery(Boolean(user));
  const [searchParams] = useSearchParams();

  const [reportType, setReportType] = useState<ReportType>(() =>
    parseReportType(searchParams.get("type"), user?.role),
  );
  const { period: month, setPeriod: setMonth, months } = useReportingPeriod();
  const [donorId, setDonorId] = useState(searchParams.get("donor") ?? "");
  const [includeStudents, setIncludeStudents] = useState(false);
  const [managementNote, setManagementNote] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [groupCode, setGroupCode] = useState("");
  const [previewRequested, setPreviewRequested] = useState(true);
  const programmes = useProgrammesOverviewQuery(Boolean(user), month);

  const visibleDonors = donors.data?.donors ?? [];
  const selectedDonorId = donorId || visibleDonors[0]?.id || "";

  const filters = useMemo(() => {
    if (!previewRequested || !user) {
      return null;
    }
    return buildReportFilters({
      reportType,
      month,
      groupCode:
        reportType === ReportType.BOARD || reportType === ReportType.PROGRAMME
          ? groupCode || undefined
          : undefined,
      donorId: reportType === ReportType.DONOR ? selectedDonorId : undefined,
      includeStudents: reportType === ReportType.DONOR ? includeStudents : undefined,
      managementNote: managementNote || undefined,
    });
  }, [
    previewRequested,
    user,
    reportType,
    month,
    groupCode,
    selectedDonorId,
    includeStudents,
    managementNote,
  ]);

  const preview = useReportPreviewQuery(filters);
  const exportReport = useExportReportMutation();

  const canExport = user?.permissions.actions.includes(AppAction.REPORT_EXPORT);

  function refreshPreview() {
    setPreviewRequested(true);
  }

  async function handleExport(format: "csv" | "pdf") {
    if (!filters) {
      return;
    }
    await exportReport.mutateAsync({ format, filters });
  }

  function handlePrint() {
    window.print();
  }

  function openNoteModal() {
    setNoteDraft(managementNote);
    setNoteModalOpen(true);
  }

  function saveNote() {
    setManagementNote(noteDraft);
    setPreviewRequested(true);
    setNoteModalOpen(false);
  }

  return (
    <div>
      <PageHeader
        eyebrow="Reports"
        title="Reports worth sharing"
        description="Choose a report, check the details, then download or print."
        actions={
          <>
            <MonthSelector
              value={month}
              months={months}
              onChange={(value) => {
                setMonth(value);
                setPreviewRequested(true);
              }}
            />
            {canExport ? (
              <>
                <Button
                  variant="secondary"
                  disabled={!filters || exportReport.isPending}
                  onClick={() => {
                    void handleExport("csv");
                  }}
                >
                  Export CSV
                </Button>
                <Button
                  disabled={!preview.data?.report}
                  onClick={handlePrint}
                >
                  Print / Save PDF
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <Card padding="md" className="report-options-panel mb-6">
        <div className="grid gap-[18px] lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <div className="grid gap-[14px] sm:grid-cols-2">
            {REPORT_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={[
                  "rounded-xl border px-5 py-5 text-left transition-colors",
                  reportType === option.id
                    ? "border-[#2a866b] bg-[#f5faf7] shadow-[0_0_0_1px_#2a866b10]"
                    : "border-edlab-line text-[#7c8990] hover:bg-edlab-bg",
                ].join(" ")}
                onClick={() => {
                  setReportType(option.id);
                  setManagementNote("");
                  setPreviewRequested(true);
                }}
              >
                <b className="block text-[15px] font-semibold text-[#35434a]">{option.label}</b>
                <p className="mt-[5px] text-xs">{option.description}</p>
              </button>
            ))}
          </div>

          <div className="grid gap-3">
            {reportType === ReportType.BOARD || reportType === ReportType.PROGRAMME ? (
              <ChipGroup>
                <Chip
                  active={!groupCode}
                  onClick={() => {
                    setGroupCode("");
                    setPreviewRequested(true);
                  }}
                >
                  All programmes
                </Chip>
                {programmes.data?.groups.map((group) => (
                  <Chip
                    key={group.id}
                    active={groupCode === group.code}
                    onClick={() => {
                      setGroupCode(group.code);
                      setPreviewRequested(true);
                    }}
                  >
                    {group.code === "dlp" ? "DLP" : group.name}
                  </Chip>
                ))}
              </ChipGroup>
            ) : null}

            {reportType === ReportType.DONOR ? (
              <>
                <label className="grid gap-1 text-sm">
                  Donor
                  <select
                    className="edlab-input"
                    value={selectedDonorId}
                    onChange={(e) => {
                      setDonorId(e.target.value);
                      setPreviewRequested(true);
                    }}
                  >
                    {visibleDonors.map((donor) => (
                      <option key={donor.id} value={donor.id}>
                        {donor.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={includeStudents}
                    onChange={(e) => {
                      setIncludeStudents(e.target.checked);
                      setPreviewRequested(true);
                    }}
                  />
                  Include supported students
                </label>
              </>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={openNoteModal}>
                {managementNote ? "Edit management note" : "Add management note"}
              </Button>
              {managementNote ? (
                <span className="text-xs text-edlab-muted">Note will appear in the report</span>
              ) : null}
            </div>

            <Button variant="secondary" onClick={refreshPreview}>
              Refresh preview
            </Button>
          </div>
        </div>
      </Card>

      {preview.isLoading ? (
        <LoadingSpinner label="Preparing report preview…" className="py-16" />
      ) : preview.isError ? (
        <EmptyState
          title="Could not prepare report"
          description="Check filters and ensure the API is running."
        />
      ) : preview.data?.report ? (
        <ReportPreviewDisplay report={preview.data.report} />
      ) : (
        <EmptyState
          title="Choose a report to preview"
          description="Select a report type and reporting month to generate a scoped preview."
        />
      )}

      <Modal
        open={noteModalOpen}
        onClose={() => {
          setNoteModalOpen(false);
        }}
        title="Management note"
        description="This note will appear in the current report."
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setNoteModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={saveNote}>Apply note</Button>
          </>
        }
      >
        <label className="form-field">
          Note
          <textarea
            className="edlab-textarea"
            rows={5}
            value={noteDraft}
            placeholder="Optional context for board or donor readers…"
            onChange={(event) => {
              setNoteDraft(event.target.value);
            }}
          />
        </label>
      </Modal>
    </div>
  );
}
